import React, { useEffect, useState, useMemo, useRef } from 'react'
import styled from 'particles/themes/styled'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Formik, Form, FormikProps } from 'formik'
import * as yup from 'yup'
import { theme } from 'theme'
import Button from 'components/Shared/Button/OldButton'
import { getCampaignSummary, CampaignState, closeCampaign } from 'state/campaigns'
import CampaignHeader from './CampaignHeader'
import { FormikRadioButtonGroup } from 'particles/RadioButton/Group'
import { FormikTextField } from 'particles/TextInputs/TextField'
import i18n from 'config/i18n'
import { selectCampaignSummary, selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import Loading from 'components/Loading'
import Modal from 'particles/Modal'
import Text from 'particles/Text'
import Checkbox from 'particles/Checkbox'

export const REASON_OPTIONS = [
    {
        value: 'duration_ended',
        label: i18n.t('submission.closeCampaign.campaignEndedReason'),
    },
    { value: 'brand_decision', label: i18n.t('submission.closeCampaign.brandDescisionReason') },
    {
        value: 'finance_admin_purpose',
        label: i18n.t('submission.closeCampaign.financeAdminReason'),
    },
]

const TooltipButton = styled(Button)`
    &:hover div {
        font-weight: bold;
        color: ${theme.colors.monza};
    }
`

interface CloseCampaignProps {
    campaign: CampaignState
}

type FormValues =
    | {
          unpaid: boolean
          inProgress: boolean
          reason?: undefined
          keyword?: undefined
      }
    | {
          reason: string
          keyword: string
          unpaid?: undefined
          inProgress?: undefined
      }

const StepContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[400]};
`

const CloseCampaign: React.FC<CloseCampaignProps> = ({ campaign }) => {
    const [isOpen, setIsOpen] = useState(false)
    const formikRef = useRef<FormikProps<FormValues>>(null)
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const campaignSummary = useSelector(selectCampaignSummary(campaign.uuid))
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.CampaignSummary]))
    const [currentStep, setCurrentStep] = useState<number>(0)
    const closeKeyword = t('submission.closeCampaign.closeKeyword', 'CLOSE CAMPAIGN')

    let unpaidCount = 0
    let inProgressCount = 0
    if (!isLoading && campaignSummary && campaignSummary.submission_summary) {
        unpaidCount = campaignSummary.submission_summary.finished_count
        inProgressCount = campaignSummary.submission_summary.in_progress_count
    }

    const handleConfirm = () => {
        if (formikRef.current) {
            formikRef.current.submitForm()
        }
    }

    useEffect(() => {
        return () => setCurrentStep(0)
    }, [])

    const initialValues = useMemo(() => {
        if (currentStep === 0) {
            return {
                unpaid: false,
                inProgress: false,
            }
        } else {
            return {
                reason: '',
                keyword: '',
            }
        }
    }, [unpaidCount, inProgressCount, currentStep])

    const validationSchema = useMemo(() => {
        const result: any = {}
        if (currentStep === 0) {
            if (unpaidCount > 0) {
                result.unpaid = yup.boolean().oneOf([true])
            }
            if (inProgressCount > 0) {
                result.inProgress = yup.boolean().oneOf([true])
            }
        } else {
            const errorMessage = t('submission.closeCampaign.typeKeywordUppercaseInstruction', {
                defaultValue: 'Please type {{closeKeyword}} in uppercase',
                closeKeyword,
            })
            result.reason = yup.string().required(t('errorMessage.validation.selectReason', 'Please select a reason'))
            result.keyword = yup
                .string()
                .required(errorMessage)
                .matches(new RegExp(`^${closeKeyword}$`), errorMessage)
        }
        return yup.object(result)
    }, [unpaidCount, inProgressCount, currentStep])

    useEffect(() => {
        if (isOpen) {
            dispatch(
                getCampaignSummary({
                    uuid: campaign.uuid,
                }),
            )
        }
    }, [isOpen])

    return (
        <>
            <TooltipButton
                p={0}
                m={0}
                width="100%"
                textProps={{ fontWeight: 'normal', color: theme.colors.monza, textAlign: 'left' }}
                onClick={() => {
                    setIsOpen(true)
                }}
            >
                {t('button.closeCampaign', 'Close campaign')}
            </TooltipButton>
            {isOpen && (
                <Modal
                    onClose={() => {
                        setCurrentStep(0)
                        setIsOpen(false)
                    }}
                    onConfirm={handleConfirm}
                    size="m"
                    title={
                        currentStep === 0
                            ? t('modalWindow.confirmationMessage.closeCampaignTitle')
                            : t('modalWindow.confirmationMessage.confirmCloseCampaignTitle')
                    }
                    hasIcon={false}
                    primaryButtonProps={{
                        label: currentStep === 0 ? t('button.continue') : t('button.closeCampaign'),
                    }}
                    subtext={
                        !isLoading && currentStep === 0 && unpaidCount === 0 && inProgressCount === 0
                            ? t('modalWindow.closeCampaignConfirmationDescription')
                            : ''
                    }
                    secondaryButtonProps={{
                        label: t('button.cancel'),
                    }}
                    isButtonSetFixed
                >
                    {isLoading ? (
                        <Loading />
                    ) : (
                        <Formik
                            innerRef={formikRef}
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            enableReinitialize={true}
                            onSubmit={values => {
                                if (currentStep === 0) {
                                    setCurrentStep(1)
                                } else {
                                    const { reason } = values
                                    dispatch(closeCampaign({ uuid: campaign.uuid, reason, forceClose: true }))
                                }
                            }}
                        >
                            {(formikBag: FormikProps<FormValues>) => (
                                <Form>
                                    <>
                                        {currentStep === 0 ? (
                                            unpaidCount > 0 || inProgressCount > 0 ? (
                                                <StepContainer>
                                                    <Text>{t('submission.closeCampaign.closeIssueHeader')}</Text>

                                                    <div>
                                                        {unpaidCount > 0 && (
                                                            <Text>
                                                                {t('modalWindow.closeCampaignUnpaidSubmissionStatus', {
                                                                    unpaidCount: unpaidCount,
                                                                })}
                                                            </Text>
                                                        )}
                                                        {inProgressCount > 0 && (
                                                            <Text>
                                                                {t(
                                                                    'modalWindow.closeCampaignInProgressSubmissionStatus',
                                                                    { inProgressCount: inProgressCount },
                                                                )}
                                                            </Text>
                                                        )}
                                                    </div>
                                                    {unpaidCount > 0 && inProgressCount > 0 ? (
                                                        <Text>
                                                            {t(
                                                                'submission.closeCampaign.payRejectSubmissionInstruction',
                                                            )}
                                                        </Text>
                                                    ) : inProgressCount > 0 ? (
                                                        <Text>
                                                            {t('submission.closeCampaign.rejectSubmissionInstruction')}
                                                        </Text>
                                                    ) : (
                                                        <Text>
                                                            {t('submission.closeCampaign.paySubmissionInstruction')}
                                                        </Text>
                                                    )}
                                                    <Text>{t('submission.closeCampaign.confirmInstruction')}</Text>
                                                    <div>
                                                        {unpaidCount > 0 && (
                                                            <Checkbox
                                                                name="unpaid"
                                                                label={t(
                                                                    'modalWindow.closeCampaignUnpaidSubmissionToBeRejectedConfirmation',
                                                                )}
                                                                onChange={() => {
                                                                    formikBag.setFieldValue(
                                                                        'unpaid',
                                                                        !formikBag.values?.unpaid,
                                                                    )
                                                                }}
                                                                checkbox={
                                                                    formikBag.values?.unpaid
                                                                        ? 'selected'
                                                                        : 'notSelected'
                                                                }
                                                                size="s"
                                                            />
                                                        )}
                                                        {inProgressCount > 0 && (
                                                            <Checkbox
                                                                name="inProgress"
                                                                label={t(
                                                                    'modalWindow.closeCampaignInProgressSubmissionToBeRejectedConfirmation',
                                                                )}
                                                                onChange={() => {
                                                                    formikBag.setFieldValue(
                                                                        'inProgress',
                                                                        !formikBag.values?.inProgress,
                                                                    )
                                                                }}
                                                                checkbox={
                                                                    formikBag.values?.inProgress
                                                                        ? 'selected'
                                                                        : 'notSelected'
                                                                }
                                                                size="s"
                                                            />
                                                        )}
                                                    </div>
                                                    {!formikBag.isValid && (
                                                        <Text color="error">
                                                            {t('errorMessage.validation.tickBox')}
                                                        </Text>
                                                    )}
                                                </StepContainer>
                                            ) : null
                                        ) : (
                                            <StepContainer>
                                                <CampaignHeader campaign={campaign} />
                                                <FormikRadioButtonGroup
                                                    name="reason"
                                                    options={REASON_OPTIONS}
                                                    size="s"
                                                    title={t(
                                                        'submission.closeCampaign.selectCloseCampaignReasonHeader',
                                                    )}
                                                />
                                                <FormikTextField
                                                    name="keyword"
                                                    autoComplete="off"
                                                    label={t('submission.closeCampaign.closeInstruction', {
                                                        closeKeyword,
                                                    })}
                                                    size="s"
                                                    placeholder={t('submission.closeCampaign.typeKeywordInstruction', {
                                                        closeKeyword,
                                                    })}
                                                />
                                                <Text scale="bodyS700">
                                                    {t('modalWindow.confirmationMessage.cantUndoActionWarning')}
                                                </Text>
                                            </StepContainer>
                                        )}
                                    </>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Modal>
            )}
        </>
    )
}

export default CloseCampaign
